namespace $ {

	export type $shm_wordstat_region_row = {
		id: string
		label: string
		level: number
	}

}

namespace $.$$ {

	export class $shm_wordstat_region_select extends $.$shm_wordstat_region_select {

		section_rows() {
			return [
				this.Title(),
				this.Content(),
			] as readonly $mol_view[]
		}

		title_content() {
			return [
				this.Title_line(),
				this.Summary(),
			]
		}

		title_line_content() {
			return [
				this.Label(),
				...( this.select_all_visible() ? [ this.Select_all() ] : [] ),
			]
		}

		select_all_checks() {
			return this.allowed_check_ids().map( id => this.Row_check( id ) )
		}

		lines( text: string ) {
			return [ ... new Set(
				String( text )
					.split( /[\n;,]+/ )
					.map( item => item.trim() )
					.filter( Boolean )
			) ]
		}

		region_ids( text = this.regions() ) {
			return this.lines( text )
		}

		@ $mol_mem
		tree() {
			return this.$.$shm_wordstat_region_tree()
		}

		@ $mol_mem
		flat_tree() {
			const rows: $shm_wordstat_region_row[] = [
				{ id: 'Все', label: 'Весь мир', level: 0 },
			]
			const walk = ( nodes: readonly $shm_wordstat_region_info[], level: number )=> {
				for( const node of nodes ) {
					rows.push({
						id: String( node.id ?? '' ),
						label: String( node.label ?? node.id ?? '' ),
						level,
					})
					walk( node.children ?? [], level + 1 )
				}
			}
			walk( this.tree(), 1 )
			return rows
		}

		@ $mol_mem
		allowed_set(): Set< string > | null {
			const source = this.allowed_ids().map( String )
			if( !source.length ) return null
			return new Set( source.filter( id => id && id !== 'Все' ) )
		}

		@ $mol_mem
		allowed_check_ids() {
			return this.allowed_ids().map( String ).filter( Boolean )
		}

		all_row_id() {
			return 'Все'
		}

		all_row( id: string ) {
			return id === 'Все'
		}

		node_allowed_deep( node: $shm_wordstat_region_info, allowed: Set< string > ): boolean {
			const id = String( node.id ?? '' )
			if( allowed.has( id ) ) return true
			return ( node.children ?? [] ).some( child => this.node_allowed_deep( child, allowed ) )
		}

		@ $mol_mem
		tree_filtered(): readonly $shm_wordstat_region_info[] {
			const allowed = this.allowed_set()
			if( !allowed ) return this.tree()
			if( !allowed.size ) return []
			return this.tree().filter( node => this.node_allowed_deep( node, allowed ) )
		}

		@ $mol_mem
		node_by_id() {
			const next = new Map< string, $shm_wordstat_region_info >()
			const walk = ( nodes: readonly $shm_wordstat_region_info[] )=> {
				for( const node of nodes ) {
					next.set( String( node.id ?? '' ), node )
					walk( node.children ?? [] )
				}
			}
			walk( this.tree() )
			return next
		}

		@ $mol_mem
		label_by_id() {
			const next = new Map< string, string >()
			for( const row of this.flat_tree() ) next.set( row.id, row.label )
			return next
		}

		label_for( ids: readonly string[] ) {
			if( !ids.length ) return '—'
			const labels_by_id = this.$.$mol_error_fence(
				()=> this.label_by_id(),
				()=> new Map< string, string >(),
				()=> new Map< string, string >(),
			)
			const labels = ids.map( id => id === 'Все' ? 'Весь мир' : labels_by_id.get( id ) ?? id )
			if( labels.length <= 3 ) return labels.join( ', ' )
			return `${ labels.slice( 0, 3 ).join( ', ' ) } +${ labels.length - 3 }`
		}

		summary() {
			return this.label_for( this.region_ids() )
		}

		previous_label() {
			return this.summary()
		}

		current_label() {
			return this.summary()
		}

		@ $mol_mem
		filtered_rows() {
			const needle = this.search().trim().toLowerCase()
			const rows = this.flat_tree().filter( row => this.row_available( row.id ) )
			if( !needle ) return rows
			return rows.filter( row => row.label.toLowerCase().includes( needle ) || row.id.includes( needle ) )
		}

		row_available( id: string ) {
			const allowed = this.allowed_set()
			if( !allowed ) return true
			if( this.all_row( id ) ) return this.allowed_check_ids().includes( id )
			if( allowed.has( id ) ) return true
			const node = this.node_by_id().get( id )
			return node ? this.node_allowed_deep( node, allowed ) : false
		}

		presets_content() {
			return this.presets_visible() ? [ this.Preset_actions() ] : []
		}

		preset_level_ids( level: number ) {
			return this.flat_tree()
				.filter( row => row.id && row.level === level )
				.map( row => row.id )
		}

		district_ids() {
			return [ '3', '17', '26', '102444', '40', '52', '59', '73', '977' ]
		}

		area_ids() {
			const districts = new Set( this.district_ids() )
			return this.flat_tree()
				.filter( row => row.level === 3 )
				.filter( row => {
					const parent = this.parent_id( row.id )
					return parent ? districts.has( parent ) : false
				} )
				.map( row => row.id )
		}

		preset_available_ids( ids: readonly string[] ) {
			const allowed = this.allowed_set()
			return allowed ? ids.filter( id => allowed.has( id ) ) : [ ... ids ]
		}

		preset_enable_ids() {
			const allowed = this.allowed_set()
			return !allowed || allowed.has( '225' ) ? [ '225' ] : []
		}

		@ $mol_mem
		parent_by_id() {
			const next = new Map< string, string >()
			const walk = ( nodes: readonly $shm_wordstat_region_info[], parent = '' )=> {
				for( const node of nodes ) {
					const id = String( node.id ?? '' )
					next.set( id, parent )
					walk( node.children ?? [], id )
				}
			}
			walk( this.tree() )
			return next
		}

		parent_id( id: string ) {
			return this.parent_by_id().get( id ) ?? ''
		}

		selected_parent_ids() {
			const next = new Set< string >()
			for( const id of this.region_ids() ) {
				if( this.all_row( id ) ) continue

				let parent = this.parent_id( id )
				while( parent ) {
					next.add( parent )
					parent = this.parent_id( parent )
				}
			}
			return next
		}

		node_ids_deep() {
			return this.flat_tree().map( row => row.id ).filter( Boolean )
		}

		expand_only( ids: readonly string[] ) {
			const expanded = new Set( ids )
			for( const id of this.node_ids_deep() ) this.node_expanded( id, expanded.has( id ) )
			return null
		}

		expand_add( ids: readonly string[] ) {
			for( const id of ids ) this.node_expanded( id, true )
			return null
		}

		@ $mol_action
		preset_reset( next?: Event ) {
			if( next === undefined ) return null
			this.regions( 'Все' )
			return next
		}

		@ $mol_action
		collapse_all( next?: Event ) {
			if( next === undefined ) return null
			this.expand_only( [] )
			return next
		}

		preset_checked( preset: readonly string[], next?: boolean, enable: readonly string[] = [] ) {
			const selected = new Set( this.region_ids() )
			if( next === undefined ) return preset.length > 0 && preset.every( id => selected.has( id ) )

			for( const id of preset ) {
				if( next ) selected.add( id )
				else selected.delete( id )
			}
			if( next ) for( const id of enable ) selected.add( id )
			this.regions([ ... selected ].join( '\n' ) )
			return next
		}

		preset_districts_checked( next?: boolean ) {
			const checked = this.preset_checked(
				this.preset_available_ids( this.district_ids() ),
				next,
				this.preset_enable_ids(),
			)
			if( next ) this.expand_add([ '225' ])
			return checked
		}

		preset_areas_checked( next?: boolean ) {
			const checked = this.preset_checked(
				this.preset_available_ids( this.area_ids() ),
				next,
				this.preset_enable_ids(),
			)
			if( next ) this.expand_add([ '225', ... this.district_ids() ])
			return checked
		}

		preset_districts_color() {
			return this.preset_districts_checked() ? 'var(--mol_theme_current)' : ''
		}

		preset_areas_color() {
			return this.preset_areas_checked() ? 'var(--mol_theme_current)' : ''
		}

		tree_content() {
			return this.$.$mol_error_fence(
				()=> {
					if( this.search().trim() ) {
						const rows = this.filtered_rows()
						return rows.length
							? rows.map( row => this.Row( row.id ) )
							: [ this.Empty() ]
					}

					return [
						... this.row_available( this.all_row_id() ) ? [ this.Node( this.all_row_id() ) ] : [],
						... this.tree_filtered().map( node => this.Node( String( node.id ?? '' ) ) ),
					]
				},
				error => [ this.error_row( error ) ],
				promise => $mol_fail_hidden( promise ),
			)
		}

		error_row( error: Error ) {
			this.error_text( this.$.$mol_error_message( error ) )
			return this.Error()
		}

		row_data( id: string ) {
			if( this.all_row( id ) ) return { id, label: 'Весь мир', level: 0 }
			return this.filtered_rows().find( row => row.id === id ) ?? { id, label: id, level: 0 }
		}

		node_data( id: string ) {
			if( this.all_row( id ) ) return null
			return this.node_by_id().get( id ) ?? null
		}

		node_rows( id: string ) {
			return [
				this.Row( id ),
				... this.node_expandable( id ) && this.node_expanded( id ) ? [ this.Children( id ) ] : [],
			] as readonly $mol_view[]
		}

		node_children( id: string ) {
			const allowed = this.allowed_set()
			if( this.all_row( id ) ) return []
			return ( this.node_data( id )?.children ?? [] )
				.filter( child => !allowed || this.node_allowed_deep( child, allowed ) )
				.map( child => this.Node( String( child.id ?? '' ) ) )
		}

		node_expandable( id: string ) {
			return this.node_children( id ).length > 0
		}

		@ $mol_mem_key
		node_expanded( id: string, next?: boolean ): boolean {
			const key = `${ this }.node_expanded:${ id }`
			if( next !== undefined ) return this.$.$mol_state_local.value< boolean >( key, next ) ?? false

			const stored = this.$.$mol_state_local.value< boolean >( key )
			if( stored != null ) return stored

			return this.selected_parent_ids().has( id )
		}

		row_title( id: string ) {
			const row = this.row_data( id )
			return row.id ? `${ row.label }` : row.label
		}

		row_checkable( id: string ) {
			const allowed = this.allowed_check_ids()
			if( this.all_row( id ) ) return allowed.length ? allowed.includes( id ) : true
			return allowed.length ? allowed.includes( id ) : true
		}

		row_content( id: string ) {
			if( this.all_row( id ) ) return [
				this.Row_check( id ),
				... this.only_visible() ? [ this.Row_only( id ) ] : [],
				... this.select_all_visible() ? [] : [ this.Row_collapse( id ) ],
			]

			return this.row_checkable( id )
				? [ this.Row_check( id ), ... this.only_visible() ? [ this.Row_only( id ) ] : [] ]
				: [ this.Row_label( id ) ]
		}

		@ $mol_action
		row_only( id: string, next?: Event ) {
			if( next === undefined ) return null
			this.regions( id )
			return next
		}

		row_indent( id: string ) {
			return this.row_data( id ).level * 20
		}

		row_checked( id: string, next?: boolean ) {
			const ids = new Set( this.region_ids() )
			if( next === undefined ) return ids.has( id )

			if( next ) ids.add( id )
			else ids.delete( id )
			this.regions([ ... ids ].join( '\n' ) )
			return next
		}

	}

}
